import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { DateRangeFilter, useDateRange, rangeLabel } from "@/components/kit/DateRangeFilter";
import { MetricChart } from "@/components/kit/MetricChart";
import { useTheme } from "@/context/ThemeContext";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { fmtMoney, fmtNumber, fmtDateTime, initials, getDisplayTimezone } from "@/lib/format";
import {
  Wallet, Banknote, TrendingUp, MousePointerClick,
  Clock, XCircle, Percent,
  Send, Mail, Phone, Hash, Lock, UserPlus, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveAsset } from "@/components/kit/FileUpload";
import { WithdrawFlow } from "@/drawers/WithdrawFlow";

function ManagerCard({ manager }) {
  if (!manager) return null;
  const tgHandle = (manager.telegram || "").replace(/^@/, "");
  const contacts = [
    manager.telegram && { icon: Send, label: manager.telegram, href: `https://t.me/${tgHandle}` },
    manager.discord && { icon: Hash, label: manager.discord },
    manager.email && { icon: Mail, label: manager.email, href: `mailto:${manager.email}` },
    manager.phone && { icon: Phone, label: manager.phone, href: `tel:${manager.phone}` },
  ].filter(Boolean);
  const primary = contacts.find((x) => x.href);
  const av = resolveAsset(manager.avatar);
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="buyer-manager-card">
      <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Your manager</div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white/[0.08] text-sm font-medium text-foreground">
          {av ? <img src={av} alt="" className="h-full w-full object-cover" /> : initials(manager.name || "M")}
        </div>
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{manager.name}</div>
          <div className="text-xs text-muted-foreground">{manager.role === "tenant_admin" ? "Account owner" : "Account manager"}</div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {contacts.map((ct, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
            <ct.icon className="h-3.5 w-3.5 text-muted-foreground/70" />
            <span className="truncate text-foreground/90">{ct.label}</span>
          </div>
        ))}
      </div>
      {primary && (
        <a href={primary.href} target="_blank" rel="noreferrer">
          <Button data-testid="buyer-contact-manager" className="mt-4 w-full gap-2 bg-white text-black hover:bg-white/90">
            <Send className="h-4 w-4" /> Contact manager
          </Button>
        </a>
      )}
    </div>
  );
}

export function BuyerDashboard() {
  const { user } = useAuth();
  const range = useDateRange("dash-range-buyer");
  const { theme } = useTheme();
  const light = theme === "light";
  const { data } = useQuery({ queryKey: ["dashboard", range.from, range.to, getDisplayTimezone()], queryFn: async () => (await http.get("/dashboard", { params: { from: range.from, to: range.to, tz: getDisplayTimezone() } })).data });
  const [wd, setWd] = useState(false);
  const c = data?.cards || {};

  const perfCards = [
    { label: "Profit", value: fmtMoney(c.profit ?? c.earned_total), icon: TrendingUp, accent: "#22C55E", className: "col-span-2" },
    { label: "Clicks", value: fmtNumber(c.clicks), icon: MousePointerClick, accent: "#A1A1AA" },
    { label: "Leads", value: fmtNumber(c.leads), icon: UserPlus, accent: "#3B82F6" },
    { label: "Deposits", value: fmtNumber(c.deposits), icon: Banknote, accent: "#22C55E" },
    { label: "Rejects", value: fmtNumber(c.rejects), icon: XCircle, accent: "#EF4444" },
    { label: "CR", value: `${fmtNumber(c.cr)}%`, icon: Percent, accent: "#A1A1AA" },
    { label: "EPC", value: fmtMoney(c.epc), icon: Activity, accent: "#A1A1AA" },
  ];
  const balanceTiles = [
    { label: "Hold Balance", value: fmtMoney(c.hold_balance), icon: Lock, accent: "#EAB308" },
    { label: "Pending withdrawals", value: fmtMoney(c.pending_withdrawals), icon: Clock, accent: "#EAB308" },
    { label: "Total earned", value: fmtMoney(c.earned_total), icon: TrendingUp, accent: "#A1A1AA" },
  ];
  const canPayout = !!c.can_request_payout;

  return (
    <div className="space-y-6" data-testid="buyer-dashboard">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} subtitle="Showing your personal performance only." />
        <DateRangeFilter range={range} />
      </div>

      {/* Balance hero + manager */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className={`lg:col-span-2 rounded-2xl border p-6 ${light
          ? "border-[#D6EFDF] bg-gradient-to-br from-[#EAF8F0] to-white"
          : "border-white/[0.08] bg-gradient-to-br from-[#16261b] via-[#141417] to-[#0A0A0A]"}`} data-testid="buyer-balance-hero">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#22C55E]">
                <Wallet className="h-4 w-4" /> Available balance
              </div>
              <div className={`mt-2 font-display text-4xl font-bold sm:text-5xl ${light ? "text-[#15803D]" : "text-white"}`} data-testid="buyer-available-amount">{fmtMoney(c.available_balance ?? c.withdrawable)}</div>
            </div>
            <div className="text-right">
              <Button onClick={() => setWd(true)} disabled={!canPayout} data-testid="request-payout-btn"
                className="gap-2 bg-[#22C55E] text-black hover:bg-[#16a34a] disabled:opacity-40">
                <Banknote className="h-4 w-4" /> Request payout
              </Button>
              <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid="min-payout-hint">
                {canPayout ? "Withdraw from your Available Balance" : `Minimum payout: ${fmtMoney(c.min_payout)}`}
              </div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {balanceTiles.map((t) => (
              <div key={t.label} className="rounded-lg border border-white/[0.06] bg-white/[0.03] p-3" data-testid={`buyer-balance-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><t.icon className="h-3 w-3" />{t.label}</div>
                <div className="mt-1 font-mono text-lg font-semibold" style={{ color: t.accent }}>{t.value}</div>
              </div>
            ))}
          </div>
        </div>
        <ManagerCard manager={data?.manager} />
      </div>

      <WithdrawFlow open={wd} onOpenChange={setWd} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {perfCards.map((card, i) => (
          <StatCard key={card.label} {...card} delay={i * 40} testId={`buyer-stat-${card.label.toLowerCase().replace(/[^a-z]/g, "-")}`} />
        ))}
      </div>

      <MetricChart
        title={`My performance — ${rangeLabel(range)}`}
        data={data?.series || []}
        metrics={[
          { key: "clicks", label: "Clicks", color: "#A1A1AA" },
          { key: "leads", label: "Leads", color: "#3B82F6" },
          { key: "deposits", label: "Deposits", color: "#22C55E" },
          { key: "profit", label: "Profit", color: "#EAB308" },
        ]}
        defaultActive={["clicks", "deposits", "profit"]}
        testId="buyer-chart"
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="buyer-recent-conversions">
          <h3 className="mb-3 text-sm font-medium text-foreground">Latest Conversions</h3>
          <div className="space-y-1">
            {(data?.recent_conversions || []).map((cv) => (
              <div key={cv.id} className="flex items-center justify-between border-b border-white/[0.05] py-2 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="truncate text-foreground/90">{cv.offer_name}</div>
                  <div className="text-xs text-muted-foreground">{cv.country} · {fmtDateTime(cv.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[#22C55E]">{fmtMoney(cv.payout)}</span>
                  <StatusBadge status={cv.status} />
                </div>
              </div>
            ))}
            {(data?.recent_conversions || []).length === 0 && <p className="text-sm text-muted-foreground">No conversions yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="buyer-recent-withdrawals">
          <h3 className="mb-3 text-sm font-medium text-foreground">Latest Withdrawals</h3>
          <div className="space-y-1">
            {(data?.recent_withdrawals || []).map((w) => (
              <div key={w.id} className="flex items-center justify-between border-b border-white/[0.05] py-2 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="truncate text-foreground/90">{w.method}</div>
                  <div className="text-xs text-muted-foreground">{fmtDateTime(w.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-foreground">{fmtMoney(w.amount)}</span>
                  <StatusBadge status={w.status} />
                </div>
              </div>
            ))}
            {(data?.recent_withdrawals || []).length === 0 && <p className="text-sm text-muted-foreground">No withdrawals yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
